/**
 * Branded domains → product surfaces at `/` (same canonical components as hub routes).
 *
 * Canonical apex mapping (hub route in parentheses):
 * - xo.watch, www → Hub (`/`)
 * - xo-pulse.com, www → Pulse (`/pulse`)
 * - xo-digest.com, www → Digest (`/digest`)
 * - xoledger.com, www → Ledger (`/ledger`)
 * - xotraits.com, www → Traits (`/traits`)
 * - xo-vault.com, www → Vault (`/vault`)
 *
 * Future `*.xo.watch` subdomain plan (same SPA; DNS can point each host here):
 * - vault.xo.watch → storage (`/vault`)
 * - seals.xo.watch → characters (`/drops/eighth_seal` provisional)
 * - traits.xo.watch → evolution (`/traits`)
 * - pulse.xo.watch → dispatch (`/pulse`)
 * - digest.xo.watch → timeline (`/digest`)
 * - ledger.xo.watch → records (`/ledger`)
 * - bridge.xo.watch → bridge (`/bridge` — branded root only until a hub route ships)
 *
 * Multi-domain verify / notary / ledger / artifact / peer fronts: see
 * ``lib/trustStackVerificationHosts.ts`` (and ``navigation/surfaceDomains.ts`` re-exports).
 *
 * Central surface family (product line · identity role — for future headers / split deploys):
 * - XO Watch · hub
 * - XO Vault · storage
 * - XO Ledger · records
 * - XO Pulse · dispatch
 * - XO Digest · timeline
 * - XO Traits · evolution
 * - XO Seals · characters
 * - XO Bridge · bridge
 */

import { normalizeHostname } from "./host_root_landing";

const FALLBACK_PUBLIC_HUB = "https://xo.watch";

/**
 * Canonical product hub for cross-links (build-time override).
 * Set in the frontend build env, e.g.:
 *   VITE_PUBLIC_HUB_URL=https://xo.watch
 */
export function getPublicHubUrl(): string {
  const raw = String(import.meta.env.VITE_PUBLIC_HUB_URL ?? "").trim();
  if (raw) return raw.replace(/\/+$/, "");
  return FALLBACK_PUBLIC_HUB;
}

/** Identity role slug for surface chrome / SEO (not the React route string). */
export type XoSurfaceIdentityRole =
  | "hub"
  | "storage"
  | "records"
  | "dispatch"
  | "timeline"
  | "evolution"
  | "characters"
  | "bridge"
  | "constellation"
  | "moments";

export type XoSurfaceFamilyRow = {
  /** Header product line, e.g. "XO Vault". */
  productLine: string;
  /** Single-word role: storage, characters, … */
  identityRole: XoSurfaceIdentityRole;
  /** Canonical path on the hub SPA (this build). */
  hubRoute: string;
};

export type BrandedDomainSurfaceId =
  | "watch"
  | "pulse"
  | "digest"
  | "ledger"
  | "traits"
  | "vault"
  | "seals"
  | "bridge"
  | "constellation"
  | "moments";

/**
 * Canonical XO surface family — one row per routed surface id.
 * Use for future visual headers, cross-links, and subdomain documentation.
 */
export const XO_SURFACE_FAMILY: Record<BrandedDomainSurfaceId, XoSurfaceFamilyRow> = {
  watch: { productLine: "XO Watch", identityRole: "hub", hubRoute: "/" },
  vault: { productLine: "XO Vault", identityRole: "storage", hubRoute: "/vault" },
  ledger: { productLine: "XO Ledger", identityRole: "records", hubRoute: "/ledger" },
  pulse: { productLine: "XO Pulse", identityRole: "dispatch", hubRoute: "/pulse" },
  digest: { productLine: "XO Digest", identityRole: "timeline", hubRoute: "/digest" },
  traits: { productLine: "XO Traits", identityRole: "evolution", hubRoute: "/traits" },
  seals: { productLine: "XO Seals", identityRole: "characters", hubRoute: "/drops/eighth_seal" },
  bridge: { productLine: "XO Bridge", identityRole: "bridge", hubRoute: "/bridge" },
  constellation: { productLine: "XO Constellation", identityRole: "hub", hubRoute: "/constellation" },
  moments: { productLine: "21XO Moments", identityRole: "moments", hubRoute: "/moments" },
};

/**
 * Planned `*.xo.watch` hosts → same `BrandedDomainSurfaceId` as apex / hub routes.
 * Merged into `BRANDED_DOMAINS`; when DNS serves these hostnames, `/` mounts the matching surface.
 */
export const XO_WATCH_FUTURE_SUBDOMAINS: Readonly<Record<string, { id: BrandedDomainSurfaceId }>> = {
  "vault.xo.watch": { id: "vault" },
  "seals.xo.watch": { id: "seals" },
  "traits.xo.watch": { id: "traits" },
  "pulse.xo.watch": { id: "pulse" },
  "digest.xo.watch": { id: "digest" },
  "ledger.xo.watch": { id: "ledger" },
  "bridge.xo.watch": { id: "bridge" },
} as const;

const BRANDED_APEX_DOMAINS: Record<string, { id: BrandedDomainSurfaceId }> = {
  "xo.watch": { id: "watch" },
  "www.xo.watch": { id: "watch" },
  "constellation.bond": { id: "constellation" },
  "www.constellation.bond": { id: "constellation" },
  "xo.constellation.bond": { id: "constellation" },

  "xo-pulse.com": { id: "pulse" },
  "www.xo-pulse.com": { id: "pulse" },

  "xo-digest.com": { id: "digest" },
  "www.xo-digest.com": { id: "digest" },
  "xodigest.com": { id: "digest" },
  "www.xodigest.com": { id: "digest" },

  "xoledger.com": { id: "ledger" },
  "www.xoledger.com": { id: "ledger" },

  "xotraits.com": { id: "traits" },
  "www.xotraits.com": { id: "traits" },

  "xo-vault.com": { id: "vault" },
  "www.xo-vault.com": { id: "vault" },

  "xo-watch.com": { id: "watch" },
  "www.xo-watch.com": { id: "watch" },

  "xo-seals.com": { id: "seals" },
  "www.xo-seals.com": { id: "seals" },

  "xo-bridge.com": { id: "bridge" },
  "www.xo-bridge.com": { id: "bridge" },

  "moments.21xo.store": { id: "moments" },
  "www.moments.21xo.store": { id: "moments" },
};

/** Canonical host → surface id (apex + www + planned `*.xo.watch`). */
export const BRANDED_DOMAINS: Record<string, { id: BrandedDomainSurfaceId }> = {
  ...BRANDED_APEX_DOMAINS,
  ...XO_WATCH_FUTURE_SUBDOMAINS,
};

/**
 * Root (`/`) behavior on a branded host.
 * - `mount`: render the canonical surface here (default).
 * - `redirect`: send visitors elsewhere before mounting (path on this origin, or absolute URL, or `{{HUB}}/path`).
 */
export type BrandedRootPolicy =
  | { mode: "mount" }
  | { mode: "redirect"; href: string };

export type BrandedDomainSurfaceConfig = {
  id: BrandedDomainSurfaceId;
  hosts: ReadonlySet<string>;
  /** Small caps kicker (product · role) */
  kicker: string;
  /** One calm sentence — official tone, domain-specific */
  lead: string;
  resolvedHubHref: string;
  hubLabel: string;
  surfaceTitle: string;
  surfaceSubtitle: string;
  rootPolicy: BrandedRootPolicy;
  /** Stable identity metadata for future chrome (not user-facing copy alone). */
  identityRole: XoSurfaceIdentityRole;
  /** Canonical hub path for this surface in the SPA. */
  hubRoute: string;
};

type SurfaceMeta = {
  kicker: string;
  lead: string;
  hubLabel: string;
  surfaceTitle: string;
  surfaceSubtitle: string;
  /** Default `{ mode: "mount" }`. Set `redirect` to send `/` elsewhere. */
  rootPolicy?: BrandedRootPolicy;
  /** Optional: fixed hub for this surface; else `getPublicHubUrl()` */
  hubHrefOverride?: string;
};

const SURFACE_META: Record<BrandedDomainSurfaceId, SurfaceMeta> = {
  watch: {
    kicker: "XO Watch · Hub",
    lead: "Canonical hub for live surfaces, digest, pulse, and reserves.",
    hubLabel: "Canonical hub",
    surfaceTitle: "",
    surfaceSubtitle: "",
  },
  pulse: {
    kicker: "XO Pulse · Operator dispatch",
    lead: "Durable pulse artifacts from the vault-ops pulse host. Same routes and behavior as /pulse on the canonical hub.",
    hubLabel: "Canonical hub",
    surfaceTitle: "XO Pulse",
    surfaceSubtitle:
      "Live dispatch artifacts from the vault-ops pulse API — the same pulse surface as the main application, served on this domain.",
  },
  digest: {
    kicker: "XO Digest · Proof snapshot",
    lead: "Curated digest cards from the Proof API (GET /api/digest). Same read path as /digest on the canonical hub.",
    hubLabel: "Canonical hub",
    surfaceTitle: "",
    surfaceSubtitle: "",
  },
  ledger: {
    kicker: "XO Ledger · Public record",
    lead: "Read-only view of sealed witness and constellation state. Same surface as /ledger on the canonical hub.",
    hubLabel: "Canonical hub",
    surfaceTitle: "",
    surfaceSubtitle: "",
  },
  traits: {
    kicker: "XO Traits · Explorer",
    lead: "Live traits catalog and viewer context. Same surface as /traits on the canonical hub.",
    hubLabel: "Canonical hub",
    surfaceTitle: "",
    surfaceSubtitle: "",
  },
  vault: {
    kicker: "XO Vault · Verification",
    lead: "Proof reads, vault operations, and seal alignment. Same dashboard as /vault on the canonical product.",
    hubLabel: "Canonical hub",
    surfaceTitle: "XO Vault",
    surfaceSubtitle:
      "Connection plane for proof APIs, vault-ops, and seals — equivalent to /vault on the canonical host.",
  },
  seals: {
    kicker: "XO Seals · Characters",
    lead: "Seal and character-forward entry. Provisional canonical route: /drops/eighth_seal (subject to product iteration).",
    hubLabel: "Canonical hub",
    surfaceTitle: "XO Seals",
    surfaceSubtitle: "Characters — eighth-seal and seal-lineage experiences; routing may move to a dedicated `/seals` surface later.",
  },
  bridge: {
    kicker: "XO Bridge · Twin",
    lead: "Physical ↔ Digital binding layer. Connect artifacts, seals, and traits across domains.",
    hubLabel: "Canonical hub",
    surfaceTitle: "XO Bridge",
    surfaceSubtitle: "Twin binding — connect physical artifacts to digital lineage.",
  },
  moments: {
    kicker: "21XO Moments · Gifts",
    lead: "Meaningful gifts and lasting memories — the public front door for moments worth keeping.",
    hubLabel: "Canonical hub",
    surfaceTitle: "21XO Moments",
    surfaceSubtitle: "Turn meaningful moments into lasting stories.",
  },
  constellation: {
    kicker: "XO Constellation · Map",
    lead: "Public map for the XO system, artifacts, and module story.",
    hubLabel: "Open constellation",
    surfaceTitle: "XO Constellation",
    surfaceSubtitle: "Explore the system map and synced artifacts.",
    rootPolicy: { mode: "redirect", href: "/constellation" },
  },
};

function hostsSetForSurfaceId(id: BrandedDomainSurfaceId): ReadonlySet<string> {
  const hosts = new Set<string>();
  for (const [host, entry] of Object.entries(BRANDED_DOMAINS)) {
    if (entry.id === id) hosts.add(normalizeHostname(host));
  }
  return hosts;
}

/** Replace `{{HUB}}` with trimmed hub URL (no trailing slash). */
export function expandBrandedHubTemplate(template: string, hubUrl: string): string {
  const h = hubUrl.replace(/\/+$/, "");
  return template.split("{{HUB}}").join(h);
}

export type BrandedRedirectTarget =
  | { kind: "path"; path: string }
  | { kind: "external"; url: string };

export function resolveBrandedRedirectTarget(
  policy: Extract<BrandedRootPolicy, { mode: "redirect" }>,
  hubUrl: string,
): BrandedRedirectTarget {
  const expanded = expandBrandedHubTemplate(policy.href.trim(), hubUrl);
  if (expanded.startsWith("https://") || expanded.startsWith("http://")) {
    return { kind: "external", url: expanded };
  }
  const path = expanded.startsWith("/") ? expanded : `/${expanded}`;
  return { kind: "path", path };
}

export function resolveBrandedDomainSurface(hostname: string): BrandedDomainSurfaceConfig | null {
  const h = normalizeHostname(hostname);
  const entry = BRANDED_DOMAINS[h];
  if (!entry) return null;

  const meta = SURFACE_META[entry.id];
  const family = XO_SURFACE_FAMILY[entry.id];
  const resolvedHubHref = (meta.hubHrefOverride ?? getPublicHubUrl()).replace(/\/+$/, "");
  return {
    id: entry.id,
    hosts: hostsSetForSurfaceId(entry.id),
    kicker: meta.kicker,
    lead: meta.lead,
    resolvedHubHref,
    hubLabel: meta.hubLabel,
    surfaceTitle: meta.surfaceTitle,
    surfaceSubtitle: meta.surfaceSubtitle,
    rootPolicy: meta.rootPolicy ?? { mode: "mount" },
    identityRole: family.identityRole,
    hubRoute: family.hubRoute,
  };
}
